import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ClipboardList } from 'lucide-react'
import { useState } from 'react'
import { cn } from '@/lib/utils'
import { EventActivityParticipantEntry } from './eventActivityParticipantEntry'
import { EventActivityLinkedPlanData } from '../hooks/useEventActivityLinkedPlanData'
import { useActivityAssignmentViewModels } from '../hooks/useActivityAssignmentViewModels'
import ViewerParticipantAssignmentAbilitiesDialog from './ViewerParticipantAssignmentAbilitiesDialog'
import {
  hasLinkedRaidPlanRefs,
  linkedPlanRevealClassName,
} from './linkedPlanReveal'

type Props = {
  entry: EventActivityParticipantEntry
  activeActivities: wowaudit.EventActivity[]
  linkedPlanDataByActivityId: Map<number, EventActivityLinkedPlanData>
  isRaidPlansLoading?: boolean
}

const ViewerAssignmentsButton = ({
  entry,
  activeActivities,
  linkedPlanDataByActivityId,
  isRaidPlansLoading = false,
}: Props) => {
  const [openedActivityId, setOpenedActivityId] = useState<number | null>(null)
  const { openableEncounters } = useActivityAssignmentViewModels(
    entry,
    activeActivities,
    linkedPlanDataByActivityId,
  )
  const participantName = entry.participant.primaryCharacterReference.name

  if (openableEncounters.length === 0) return null

  const totalAssignmentCount = openableEncounters.reduce(
    (total, encounter) => total + encounter.totalAbilityCount,
    0,
  )
  const initialEncounter =
    openableEncounters.find((encounter) => encounter.totalAbilityCount > 0) ??
    openableEncounters[0]
  const isRevealPending =
    isRaidPlansLoading && activeActivities.some(hasLinkedRaidPlanRefs)

  return (
    <>
      <Button
        variant='outline'
        size='custom'
        className={cn(
          'flex items-center gap-2 py-0.5 px-2',
          linkedPlanRevealClassName(isRevealPending),
        )}
        onClick={() => setOpenedActivityId(initialEncounter.activity.id)}
      >
        <ClipboardList className='w-3.5 h-3.5' />
        View assignments
        {totalAssignmentCount > 0 ? (
          <Badge variant='info' className='px-1.5 py-0'>
            {totalAssignmentCount}
          </Badge>
        ) : null}
      </Button>
      <ViewerParticipantAssignmentAbilitiesDialog
        open={openedActivityId !== null}
        onOpenChange={(open) => {
          if (!open) setOpenedActivityId(null)
        }}
        encounters={openableEncounters}
        selectedActivityId={openedActivityId}
        onSelectActivity={setOpenedActivityId}
        participantName={participantName}
      />
    </>
  )
}

export default ViewerAssignmentsButton
